import { sha256Text } from "./util";

const rand = (n) => crypto.getRandomValues(new Uint8Array(n));
const b64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

export const newSalt = () => b64(rand(16));
// Legacy (pre-encryption) PIN check, used only to migrate old vaults
export const hashPin = (pin, salt) => sha256Text(`${salt}:${pin}`);

const ITER = 600000;
const MAX_TRIES = 5;

async function pinKey(pin, salt) {
  const base = await crypto.subtle.importKey("raw", new TextEncoder().encode(pin), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "PBKDF2", salt: unb64(salt), iterations: ITER, hash: "SHA-256" },
    base, { name: "AES-KW", length: 256 }, false, ["wrapKey", "unwrapKey"]);
}

export async function wrapForPin(key, pin) {
  const salt = newSalt();
  return { salt, wrapped: b64(await crypto.subtle.wrapKey("raw", key, await pinKey(pin, salt), "AES-KW")), kdf: `pbkdf2-sha256-${ITER}` };
}

export async function createVault(pin) {
  const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
  return { key, sec: await wrapForPin(key, pin) };
}

export async function openVault(pin, sec) {
  if (!sec.wrapped) return (await hashPin(pin, sec.salt)) === sec.pinHash ? { ...(await createVault(pin)), legacy: true } : null;
  try {
    return { key: await crypto.subtle.unwrapKey("raw", unb64(sec.wrapped), await pinKey(pin, sec.salt), "AES-KW", "AES-GCM", true, ["encrypt", "decrypt"]) };
  } catch {
    return null;
  }
}

export const bioCopy = async (key) =>
  crypto.subtle.importKey("raw", await crypto.subtle.exportKey("raw", key), "AES-GCM", false, ["encrypt", "decrypt"]);

export const lockoutLeft = (sec) => Math.max(0, (sec.lockUntil || 0) - Date.now());
export function afterFail(sec) {
  const fails = (sec.fails || 0) + 1;
  return { ...sec, fails, lockUntil: fails >= MAX_TRIES ? Date.now() + Math.min(3600000, 30000 * 2 ** (fails - MAX_TRIES)) : 0 };
}
export const failMessage = (sec) => {
  const w = lockoutLeft(sec);
  if (w) return `Too many attempts — try again in ${Math.ceil(w / 1000)}s`;
  const left = MAX_TRIES - sec.fails;
  return `Incorrect PIN — ${left} attempt${left === 1 ? "" : "s"} before lockout`;
};

export async function biometricAvailable() {
  if (!window.PublicKeyCredential) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

export async function registerBiometric() {
  const cred = await navigator.credentials.create({
    publicKey: {
      challenge: rand(32),
      rp: { name: "StarVault" },
      user: { id: rand(16), name: "starvault-local", displayName: "StarVault Owner" },
      pubKeyCredParams: [{ type: "public-key", alg: -7 }, { type: "public-key", alg: -257 }],
      authenticatorSelection: { authenticatorAttachment: "platform", userVerification: "required" },
      timeout: 60000,
    },
  });
  return b64(cred.rawId);
}

export async function verifyBiometric(id) {
  await navigator.credentials.get({
    publicKey: {
      challenge: rand(32),
      allowCredentials: [{ type: "public-key", id: unb64(id) }],
      userVerification: "required",
      timeout: 60000,
    },
  });
  return true;
}
